/**
 * useApiFilterOptions - Hook for fetching filter options from API endpoints
 *
 * This hook manages fetching, caching, and refreshing of filter options from APIs.
 * It's used internally by UniversalTableSearch to support API-based filters.
 *
 * Features:
 * - Automatic fetching on mount
 * - Response caching with configurable duration
 * - Optional refetch intervals for live data
 * - Error handling with graceful fallbacks
 * - Shared cache across hook instances for same endpoint
 *
 * @example
 * ```tsx
 * const buildingOptions = useApiFilterOptions({
 *   endpoint: '/building/all-ids',
 *   transform: (ids: string[]) => ids.map(id => ({ key: id, label: id }))
 * }, session);
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

interface APIFilterConfig {
  endpoint: string;
  transform?: (data: any) => Array<{ key: string; label: string }>;
  method?: 'GET' | 'POST';
  body?: any;
  cacheDuration?: number;
  refetchInterval?: number;
}

interface CacheEntry {
  data: Array<{ key: string; label: string }>;
  timestamp: number;
}

// Global cache shared across all hook instances
const apiCache = new Map<string, CacheEntry>();

// Default transform: assumes API returns string[] and maps to { key, label }
const defaultTransform = (data: any): Array<{ key: string; label: string }> => {
  if (Array.isArray(data)) {
    // If array of strings, map to { key, label }
    if (data.length === 0 || typeof data[0] === 'string') {
      return data.map((item: string) => ({ key: item, label: item }));
    }
    // If array of objects with key/label, return as-is
    if (data.length > 0 && typeof data[0] === 'object' && 'key' in data[0]) {
      return data;
    }
    // If array of objects with id, map to { key: id, label: id }
    if (data.length > 0 && typeof data[0] === 'object' && 'id' in data[0]) {
      return data.map((item: any) => ({
        key: String(item.id),
        label: item.name || item.label || String(item.id)
      }));
    }
  }

  console.warn('[useApiFilterOptions] Unexpected API response format:', data);
  return [];
};

/**
 * Hook for fetching filter options from API
 *
 * @param config - API configuration
 * @param session - Clerk session for authentication
 * @returns Object with options, loading state, error, and refetch function
 */
export const useApiFilterOptions = (
  config: APIFilterConfig | null,
  session: any
) => {
  const [options, setOptions] = useState<Array<{ key: string; label: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const refetchIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Generate cache key from endpoint and body
  const getCacheKey = useCallback((cfg: APIFilterConfig): string => {
    const bodyHash = cfg.body ? JSON.stringify(cfg.body) : '';
    return `${cfg.endpoint}:${cfg.method || 'GET'}:${bodyHash}`;
  }, []);

  // Check if cached data is still valid
  const isCacheValid = useCallback((cacheKey: string, cacheDuration: number): boolean => {
    const cached = apiCache.get(cacheKey);
    if (!cached) return false;

    const now = Date.now();
    const age = now - cached.timestamp;
    return age < cacheDuration;
  }, []);

  // Fetch options from API
  const fetchOptions = useCallback(async (cfg: APIFilterConfig, forceRefresh = false) => {
    if (!session) {
      console.log('[useApiFilterOptions] Session not ready, skipping fetch');
      return;
    }

    const cacheKey = getCacheKey(cfg);
    const cacheDuration = cfg.cacheDuration || 5 * 60 * 1000; // Default 5 minutes

    // Check cache first (unless force refresh)
    if (!forceRefresh && isCacheValid(cacheKey, cacheDuration)) {
      const cached = apiCache.get(cacheKey);
      if (cached) {
        console.log(`[useApiFilterOptions] Using cached data for ${cfg.endpoint}`);
        setOptions(cached.data);
        setError(null);
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);

      // Build full URL
      const url = cfg.endpoint.startsWith('http')
        ? cfg.endpoint
        : `${API_URL}${cfg.endpoint}`;

      console.log(`[useApiFilterOptions] Fetching from ${url}`);

      const response = await fetchWithAuth(
        url,
        {
          method: cfg.method || 'GET',
          ...(cfg.body && {
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cfg.body)
          })
        },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch options: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Transform data
      const transform = cfg.transform || defaultTransform;
      const transformedOptions = transform(data);

      console.log(`[useApiFilterOptions] Fetched ${transformedOptions.length} options from ${cfg.endpoint}`);

      // Cache the results
      apiCache.set(cacheKey, {
        data: transformedOptions,
        timestamp: Date.now()
      });

      setOptions(transformedOptions);
      setError(null);
    } catch (err) {
      console.error(`[useApiFilterOptions] Error fetching from ${cfg.endpoint}:`, err);
      setError(err as Error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [session, getCacheKey, isCacheValid]);

  // Refetch function exposed to consumers
  const refetch = useCallback(() => {
    if (config) {
      fetchOptions(config, true);
    }
  }, [config, fetchOptions]);

  // Initial fetch and refetch interval setup
  useEffect(() => {
    if (!config || !session) {
      return;
    }

    // Initial fetch
    fetchOptions(config);

    // Setup refetch interval if configured
    if (config.refetchInterval && config.refetchInterval > 0) {
      refetchIntervalRef.current = setInterval(() => {
        console.log(`[useApiFilterOptions] Refetching ${config.endpoint} (interval: ${config.refetchInterval}ms)`);
        fetchOptions(config, true);
      }, config.refetchInterval);
    }

    // Cleanup
    return () => {
      if (refetchIntervalRef.current) {
        clearInterval(refetchIntervalRef.current);
        refetchIntervalRef.current = null;
      }
    };
  }, [config, session, fetchOptions]);

  return {
    options,
    isLoading,
    error,
    refetch
  };
};

/**
 * Clear all cached API filter options
 * Useful for forcing refresh after data mutations
 */
export const clearApiFilterCache = () => {
  apiCache.clear();
  console.log('[useApiFilterOptions] Cache cleared');
};

/**
 * Clear specific cached API filter options by endpoint
 */
export const clearApiFilterCacheByEndpoint = (endpoint: string) => {
  const keysToDelete: string[] = [];

  apiCache.forEach((_, key) => {
    if (key.startsWith(endpoint)) {
      keysToDelete.push(key);
    }
  });

  keysToDelete.forEach(key => apiCache.delete(key));
  console.log(`[useApiFilterOptions] Cleared ${keysToDelete.length} cache entries for ${endpoint}`);
};
